import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import Button from '../../../components/ui/Button';

const ResourceConsumptionChart = ({ data, onZoom, onToggleSeries }) => {
  const [visibleSeries, setVisibleSeries] = useState({
    storage: true,
    apiCalls: true,
    bandwidth: true,
    compute: true
  });

  const [zoomLevel, setZoomLevel] = useState('24h');

  const toggleSeries = (seriesKey) => {
    const newVisibleSeries = {
      ...visibleSeries,
      [seriesKey]: !visibleSeries[seriesKey]
    };
    setVisibleSeries(newVisibleSeries);
    onToggleSeries?.(newVisibleSeries);
  };

  const handleZoomChange = (level) => {
    setZoomLevel(level);
    onZoom?.(level);
  };

  const seriesConfig = [
    { key: 'storage', name: 'Storage', color: '#3b82f6', unit: 'GB' },
    { key: 'apiCalls', name: 'API Calls', color: '#10b981', unit: 'K' },
    { key: 'bandwidth', name: 'Bandwidth', color: '#f59e0b', unit: 'MB/s' },
    { key: 'compute', name: 'Compute', color: '#8b5cf6', unit: 'CPU%' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-medium text-text-primary mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-text-secondary">{entry.name}</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {entry.value} {seriesConfig.find(s => s.name === entry.name)?.unit}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Resource Consumption Trends</h3>
          <p className="text-sm text-text-secondary">Real-time platform resource utilization</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            {['1h', '6h', '24h', '7d'].map((level) => (
              <Button
                key={level}
                variant={zoomLevel === level ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleZoomChange(level)}
                className="text-xs"
              >
                {level}
              </Button>
            ))}
          </div>
          
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>

      {/* Legend with Toggle */}
      <div className="flex items-center space-x-6 mb-4">
        {seriesConfig.map((series) => (
          <button
            key={series.key}
            onClick={() => toggleSeries(series.key)}
            className={`flex items-center space-x-2 px-3 py-1 rounded-md transition-all duration-150 ${
              visibleSeries[series.key] 
                ? 'bg-muted text-text-primary' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            <div 
              className={`w-3 h-3 rounded-full transition-opacity ${
                visibleSeries[series.key] ? 'opacity-100' : 'opacity-30'
              }`}
              style={{ backgroundColor: series.color }}
            />
            <span className="text-sm font-medium">{series.name}</span>
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {seriesConfig.map((series) => (
                <linearGradient key={series.key} id={`gradient-${series.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={series.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={series.color} stopOpacity={0.05}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-text-secondary)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {seriesConfig.map((series) => (
              visibleSeries[series.key] && (
                <Area
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={2}
                  fill={`url(#gradient-${series.key})`}
                  name={series.name}
                />
              )
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ResourceConsumptionChart;